import { API_CONFIG } from "../config/api";

const API_BASE_URL = API_CONFIG.BASE_URL;

export class ControlService {
  static async getAuthHeaders(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  static async createControl(data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/controls`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create control");
    }

    return response.json();
  }

  static async getControls(
    organizationId,
    businessUnitId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    let url = `${API_BASE_URL}/controls?organization_id=${organizationId}`;
    if (businessUnitId) {
      url += `&business_unit_id=${businessUnitId}`;
    }

    const response = await fetch(url, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch controls");
    }

    const data = await response.json();

    // Normalize the response to ensure consistent property names
    return Array.isArray(data)
      ? data.map((control) => {
          // Get associated risks from the correct property
          const associatedRisks = control.associated_risks || [];

          // Convert single Assignee to assigned_users array for consistency
          const assignedUsers = control.Assignee ? [control.Assignee] : [];

          return {
            ...control,
            associated_risks: associatedRisks,
            // For backward compatibility
            Risks: associatedRisks,
            risks: associatedRisks,
            // Add assigned_users array for frontend compatibility
            assigned_users: assignedUsers,
          };
        })
      : [];
  }

  static async getControlDetails(controlId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/controls/${controlId}`, {
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch control details");
    }

    const data = await response.json();

    // Normalize the response to ensure Risks is present and properly cased
    const normalizedData = {
      ...data,
      Risks: data.Risks || data.risks || [],
    };

    return normalizedData;
  }

  static async updateControl(controlId, data, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/controls/${controlId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update control");
    }

    return response.json();
  }

  static async deleteControl(controlId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(`${API_BASE_URL}/controls/${controlId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete control");
    }

    return response.json();
  }

  static async applyControlToRisk(controlId, riskId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/risks/${riskId}/controls/${controlId}`,
      {
        method: "POST",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to apply control to risk");
    }

    return response.json();
  }

  static async removeControlFromRisk(
    controlId,
    riskId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/${controlId}/remove-from-risk/${riskId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to remove control from risk");
    }

    return response.json();
  }

  // New category management methods for multiple categories system
  static async getControlCategories(controlId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/${controlId}/categories`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch control categories");
    }

    return response.json();
  }

  static async getAvailableCategories(controlId, getAccessTokenSilently) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/${controlId}/available-categories`,
      {
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch available categories"
      );
    }

    return response.json();
  }

  static async addCategoryToControl(
    controlId,
    categoryId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/${controlId}/categories/${categoryId}`,
      {
        method: "POST",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to add category to control");
    }

    return response.json();
  }

  static async removeCategoryFromControl(
    controlId,
    categoryId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/${controlId}/categories/${categoryId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to remove category from control"
      );
    }

    return response.json();
  }

  // NEW: Search controls by name, custom ID, or description
  static async searchControls(
    query,
    organizationId,
    businessUnitId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    let url = `${API_BASE_URL}/controls/search?q=${encodeURIComponent(
      query
    )}&organization_id=${organizationId}`;
    if (businessUnitId) {
      url += `&business_unit_id=${businessUnitId}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to search controls");
    }

    return response.json();
  }

  // NEW: Get controls by sub-category
  static async getControlsBySubCategory(
    subCategoryId,
    organizationId,
    businessUnitId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    let url = `${API_BASE_URL}/controls/by-sub-category/${subCategoryId}?organization_id=${organizationId}`;
    if (businessUnitId) {
      url += `&business_unit_id=${businessUnitId}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch controls by sub-category"
      );
    }

    return response.json();
  }

  // NEW: Get control by custom ID
  static async getControlByCustomId(
    customId,
    organizationId,
    getAccessTokenSilently
  ) {
    const headers = await this.getAuthHeaders(getAccessTokenSilently);
    const response = await fetch(
      `${API_BASE_URL}/controls/by-custom-id/${customId}?organization_id=${organizationId}`,
      { headers }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.error || "Failed to fetch control by custom ID"
      );
    }

    return response.json();
  }
}
